import {
  computed,
  defineStore,
  reactive,
  ref,
  useSupabase,
  useUser,
  useMetrics,
} from "#imports";
import type { Tables } from "~~/types/database.types";

export const useFollowers = defineStore("followers", () => {
  const supabase = useSupabase();
  const userStore = useUser();
  const { trackEvent } = useMetrics();

  const viewedMapCount = ref<number>(0);

  const isFetched = ref<boolean>(false);
  const followers = reactive<Tables<"followers">[]>([]);
  const fetchFollowers = async () => {
    if (!userStore.user.id) {
      followers.length = 0;
      return;
    }

    if (isFetched.value) {
      return;
    }

    const { data } = await supabase
      .from("followers")
      .select("*")
      .eq("user_id", userStore.user.id)
      .throwOnError();

    Object.assign(followers, data);
    isFetched.value = true;
  };

  const isFollower = (creator_id: string) =>
    computed(() => !!followers.find((f) => f.creator_id === creator_id));

  const addFollow = async (creator_id: string) => {
    if (!userStore.user.id) {
      throw new Error("Need to login before you can add a follow");
    }

    const { data } = await supabase
      .from("followers")
      .insert({ creator_id, user_id: userStore.user.id })
      .select("*")
      .throwOnError();
    followers.push(data[0]!);

    trackEvent("creator_followed", {
      creator_id: creator_id,
    });
  };

  const removeFollow = async (creator_id: string) => {
    if (!userStore.user.id) {
      throw new Error("Need to login before you can remove a follow");
    }
    await supabase
      .from("followers")
      .delete()
      .eq("creator_id", creator_id)
      .eq("user_id", userStore.user.id)
      .throwOnError();

    followers.splice(
      followers.findIndex((f) => f.creator_id === creator_id),
      1,
    );
  };

  return {
    isFetched,
    fetchFollowers,
    followers,
    isFollower,
    addFollow,
    removeFollow,
    viewedMapCount,
  };
});
